import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface SheetsColumnMultiFilterProps {
  label: string;
  options: readonly string[];
  selected: string[];
  onSelectedChange: (next: string[]) => void;
  formatOption: (value: string) => string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Выпадающий мультивыбор по колонке в духе Google Sheets: поиск, «выбрать всё» по видимым, чекбоксы.
 */
export function SheetsColumnMultiFilter({
  label,
  options,
  selected,
  onSelectedChange,
  formatOption,
  isOpen,
  onOpenChange,
}: SheetsColumnMultiFilterProps): React.ReactElement {
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const masterRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      return;
    }
    searchRef.current?.focus({ preventScroll: true });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isOpen, onOpenChange]);

  const q = search.trim().toLowerCase();
  const visible = useMemo(
    () => options.filter((o) => formatOption(o).toLowerCase().includes(q)),
    [options, formatOption, q],
  );

  const visibleSet = useMemo(() => new Set(visible), [visible]);

  const allVisibleSelected = visible.length > 0 && visible.every((o) => selected.includes(o));
  const someVisibleSelected = visible.some((o) => selected.includes(o)) && !allVisibleSelected;

  useEffect(() => {
    const el = masterRef.current;
    if (el) {
      el.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected, visible.length, isOpen]);

  const toggleMaster = (): void => {
    if (visible.length === 0) {
      return;
    }
    if (allVisibleSelected) {
      onSelectedChange(selected.filter((x) => !visibleSet.has(x)));
    } else {
      const merged = new Set(selected);
      for (const o of visible) {
        merged.add(o);
      }
      onSelectedChange([...merged]);
    }
  };

  const toggleOne = (value: string, checked: boolean): void => {
    if (checked) {
      onSelectedChange(selected.includes(value) ? selected : [...selected, value]);
    } else {
      onSelectedChange(selected.filter((x) => x !== value));
    }
  };

  const summary =
    selected.length === 0
      ? 'Все'
      : selected.length === 1
        ? formatOption(selected[0]!)
        : `Выбрано: ${selected.length}`;

  const titleForTrigger =
    selected.length > 1 ? selected.map(formatOption).join(', ') : selected.length === 1 ? formatOption(selected[0]!) : undefined;

  return (
    <div className="sheets-filter" ref={rootRef}>
      <span className="filters-field__label">{label}</span>
      <button
        type="button"
        className="sheets-filter__trigger"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        title={titleForTrigger}
        onClick={() => onOpenChange(!isOpen)}
      >
        <span className="sheets-filter__trigger-text">{summary}</span>
        <span className="sheets-filter__chevron" aria-hidden />
      </button>
      {isOpen ? (
        <div className="sheets-filter__panel" role="dialog" aria-label={label}>
          <input
            ref={searchRef}
            type="search"
            className="sheets-filter__search"
            placeholder="Поиск в списке…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="sheets-filter__master">
            <label className="sheets-filter__row sheets-filter__row--master">
              <input
                ref={masterRef}
                type="checkbox"
                checked={visible.length > 0 && allVisibleSelected}
                disabled={visible.length === 0}
                onChange={toggleMaster}
              />
              <span>{q ? 'Выбрать всё в результатах поиска' : 'Выбрать всё'}</span>
            </label>
          </div>
          <div className="sheets-filter__list">
            {options.length === 0 ? (
              <div className="sheets-filter__empty">Нет данных</div>
            ) : visible.length === 0 ? (
              <div className="sheets-filter__empty">Нет совпадений</div>
            ) : (
              visible.map((opt) => (
                <label key={opt === '' ? '__empty_opt' : opt} className="sheets-filter__row">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt)}
                    onChange={(e) => toggleOne(opt, e.target.checked)}
                  />
                  <span title={formatOption(opt)}>{formatOption(opt)}</span>
                </label>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
